// ---------------------------------------------------------------------------
// The period — SEN-1720 (CEO 2026-09-02: "every tile carries the same
// redundant date; we need a global date picker").
//
// A period is a BINDING, not a date: a preset ("last 90 days", "this month",
// "year to date") is relative and resolves against *now* when the page is
// read — the dashboard never stores a date it would have to keep moving
// (compute-on-read). A fixed period is an explicit half-open [start, end).
// One resolver serves every surface (dashboard toolbar, object pages, the
// agent's tools): the same words mean the same window everywhere.
// ---------------------------------------------------------------------------
import { defaultPeriod } from './engine'
import { formatDate, getTenantFiscalMonth, getTenantZone, todayInZone, tzOffsetMinutes, zonedMidnightUtc } from '../locale'

export type PeriodPreset = '7d' | '30d' | '90d' | '365d' | 'mtd' | 'ytd' | 'fytd' | 'previous_month' | '48h'
export type MasterPeriod =
  | { kind: 'preset'; preset: PeriodPreset }
  | { kind: 'fixed'; start: string; end: string }

/** `chip: false` — a widget's own window, not offered in a list's period chip.
 *  `needsFiscalMonth` — offered only once the tenant's fiscal month is known. */
export const PERIOD_PRESETS: { key: PeriodPreset; label: string; chip?: false; needsFiscalMonth?: true }[] = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: '365d', label: 'Last 365 days' },
  { key: 'mtd', label: 'Month to date' },
  { key: 'ytd', label: 'Year to date' },
  // SEN-433 T7: the tenant's fiscal year through yesterday. PeriodChip offers
  // it only once the fiscal month is known (locale.ts getTenantFiscalMonth).
  { key: 'fytd', label: 'Fiscal year to date', needsFiscalMonth: true },
  { key: 'previous_month', label: 'Previous month' },
  // 2026-10-23 (record-layout widgets): a "current" fact's window (the latest
  // weather reading) — whole hours up to the NEXT whole hour. Mirrors
  // monad/period_binding.py resolve_period.
  { key: '48h', label: 'Last 48 hours', chip: false },
]

const DAY = 86_400_000
const HOUR = 3_600_000

/** The window a period binding names, as ISO bounds. `null` = the engine's
 *  default (trailing 90 days) — the same window every view used before a
 *  picker existed, so nothing silently changes for an unbound dashboard.
 *
 *  SEN-1761: preset calendar boundaries resolve on the TENANT's clock
 *  (`tz`, defaulting to the app-boot seam getTenantZone()) — the server twin
 *  is monad/period_binding.py::resolve_period(tz=...). A Copenhagen
 *  "previous month" is the Copenhagen month: local midnights as UTC
 *  instants, 743/745 hours across a DST change. Day arithmetic moves whole
 *  LOCAL days (each boundary is its own local midnight), never n×24h.
 *  FIXED windows pass through verbatim — explicit instants are preserved.
 *
 *  SEN-433 T7: `fiscalMonth` (1-12) anchors `fytd`; it defaults to the
 *  tenant's (locale.ts), January while unknown — the server's default. */
export function resolvePeriod(
  p: MasterPeriod | null | undefined,
  now: Date = new Date(),
  tz: string = getTenantZone(),
  fiscalMonth: number = getTenantFiscalMonth() ?? 1,
): { period_start: string; period_end: string } {
  if (!p) return defaultPeriod(now, tz)
  if (p.kind === 'fixed') return { period_start: p.start, period_end: p.end }
  if (p.preset === '48h') {
    // The tenant-local whole hour (a +05:30 zone's hours are not UTC's), in instants.
    const offset = tzOffsetMinutes(tz, now) * 60_000
    const top = Math.floor((now.getTime() + offset) / HOUR) * HOUR - offset + HOUR
    return { period_start: new Date(top - 48 * HOUR).toISOString(), period_end: new Date(top).toISOString() }
  }
  const { y, m, d } = todayInZone(tz, now)
  const end = zonedMidnightUtc(tz, y, m, d)
  const back = (days: number) => zonedMidnightUtc(tz, y, m, d - days)
  let start: Date
  switch (p.preset) {
    case '7d': start = back(7); break
    case '30d': start = back(30); break
    case '90d': start = back(90); break
    case '365d': start = back(365); break
    // "To date" INCLUDES today (half-open end = tomorrow midnight): on 2 Sep
    // month-to-date is [1 Sep, 3 Sep), not the same window as on 1 Sep.
    // Trailing-N presets stop at last midnight — the incomplete day would
    // read as a dip (cold read 16).
    case 'mtd': return { period_start: zonedMidnightUtc(tz, y, m, 1).toISOString(), period_end: zonedMidnightUtc(tz, y, m, d + 1).toISOString() }
    case 'ytd': return { period_start: zonedMidnightUtc(tz, y, 0, 1).toISOString(), period_end: zonedMidnightUtc(tz, y, m, d + 1).toISOString() }
    // SEN-433 T7: [local midnight of day 1 of the fiscal start month (this
    // year once it is reached, else last year), local midnight today):
    // yesterday inclusive, Sentinel's FY to date. On the fiscal year's first
    // day the window is that day, never an empty one (as mtd on the 1st).
    // Mirrors monad/period_binding.py resolve_period; period_presets_parity.json pins both.
    case 'fytd': {
      const fm = Number.isInteger(fiscalMonth) && fiscalMonth >= 1 && fiscalMonth <= 12 ? fiscalMonth - 1 : 0
      const fy = m >= fm ? y : y - 1
      const first = zonedMidnightUtc(tz, fy, fm, 1)
      const isFirstDay = fy === y && fm === m && d === 1
      return { period_start: first.toISOString(), period_end: (isFirstDay ? zonedMidnightUtc(tz, y, m, d + 1) : end).toISOString() }
    }
    // The Monthly Store Report's window: the WHOLE calendar month before this
    // one (never "the last 30 days"). Mirrors monad/period_binding.py
    // resolve_period — on the tenant's calendar.
    case 'previous_month': return {
      period_start: zonedMidnightUtc(tz, y, m - 1, 1).toISOString(),
      period_end: zonedMidnightUtc(tz, y, m, 1).toISOString(),
    }
    default: {
      // M4: mirrors the server's deliberate `raise` (period_binding.py:132)
      // — every PERIOD_PRESETS member must have a branch above; this is not
      // reachable through the public PeriodPreset type, but a value that
      // skipped the type checker (or a stale client against a newer server)
      // must fail loudly here too, not silently fall through to whatever
      // branch happened to be last (the bug the server-side guard exists
      // for: `previous_month` used to be that unconditional fallthrough).
      const unhandled: never = p.preset
      throw new Error(`unhandled period preset: ${String(unhandled)}`)
    }
  }
  return { period_start: start.toISOString(), period_end: end.toISOString() }
}

/** The last calendar day a fixed window covers — the half-open `end` is the
 *  midnight AFTER it. The chip and its inputs speak in covered days. */
export function fixedLastDay(endIso: string): Date {
  return new Date(Date.parse(endIso) - DAY)
}
/** Inverse of fixedLastDay: the half-open end for a "to" day (YYYY-MM-DD). */
export function fixedEndFor(toDay: string): string {
  return new Date(Date.parse(`${toDay}T00:00:00Z`) + DAY).toISOString().replace('.000Z', 'Z')
}

// ── SEN-1783/1761: fixed DAY picks mean TENANT-calendar days ────────────────
// The chip's From/To inputs previously produced UTC midnights, so a
// Copenhagen tenant picking "30 Jun" bound [30 Jun 00:00Z…) — two hours into
// their local day, and unable to express the frozen oracle windows at all.
// Day → instant now goes through the SAME zone seam presets use. EXPLICIT
// stored instants are untouched; only how a picked day becomes an instant
// (and how an instant redisplays as a day) reads the tenant clock.

/** The half-open [start] instant for a "from" day: that day's TENANT-local midnight. */
export function fixedStartForDay(fromDay: string, tz: string = getTenantZone()): string {
  const [y, m, d] = fromDay.split('-').map(Number)
  return zonedMidnightUtc(tz, y, m - 1, d).toISOString()
}
/** The half-open [end) instant for a "to" day: the NEXT tenant-local midnight. */
export function fixedEndForDay(toDay: string, tz: string = getTenantZone()): string {
  const [y, m, d] = toDay.split('-').map(Number)
  return zonedMidnightUtc(tz, y, m - 1, d + 1).toISOString()
}
/** The tenant-calendar day (YYYY-MM-DD) an instant falls on. */
export function dayInTenantZone(iso: string, tz: string = getTenantZone()): string {
  const { y, m, d } = todayInZone(tz, new Date(iso))
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
/** The LAST tenant-calendar day a fixed window covers (1 ms inside the end). */
export function lastCoveredDayInTenantZone(endIso: string, tz: string = getTenantZone()): string {
  return dayInTenantZone(new Date(Date.parse(endIso) - 1).toISOString(), tz)
}

/** The chip's words for a binding — a preset by its name, a fixed window by
 *  its dates, nothing bound by the default's name. */
export function periodLabel(p: MasterPeriod | null | undefined, locale: string): string {
  if (!p) return 'Last 90 days'
  if (p.kind === 'preset') return PERIOD_PRESETS.find((x) => x.key === p.preset)?.label ?? p.preset
  // SEN-1761: the label reads the TENANT wall clock (same convention as
  // formatPeriod/compareWindowLabel) — instants stay verbatim.
  const over = { zone: getTenantZone(), locale }
  // The label names the days COVERED: a window [1 Jun, 1 Jul) reads
  // "1 Jun – 30 Jun", the same days the From/To inputs took (cold read 8).
  return `${formatDate(p.start, 'date', over)} – ${formatDate(Date.parse(p.end) - 1, 'date', over)}`
}
